import { Component, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { Tooltip } from 'primeng/tooltip';
import { filter, takeUntil, tap } from 'rxjs';
import { take } from 'rxjs/operators';

import { UnsubscribeDirective } from '@component/shared/unsubscribe.directive';
import { PaginationHelper } from '@helper/pagination.helper';
import { NotificationService } from '@service/notification.service';
import { WebsocketService } from '@service/websocket.service';
import { Notification } from '@type/notification.type';
import { Page } from '@type/page.type';

import {
  NotificationBlockComponent,
} from '@component/notifications-sidebar/notification-block/notification-block.component';

@Component({
  selector: 'app-notifications-sidebar',
  imports: [
    ButtonModule,
    Drawer,
    NotificationBlockComponent,
    Tooltip,
  ],
  templateUrl: './notifications-sidebar.component.html',
  styleUrl: './notifications-sidebar.component.scss',
})
export class NotificationsSidebarComponent extends UnsubscribeDirective {
  @ViewChild('scroller') scrollerEl!: ElementRef<HTMLDivElement>;
  private readonly messageService = inject(MessageService);
  private readonly notificationService = inject(NotificationService);
  private readonly websocketService = inject(WebsocketService);

  items = signal<Notification[]>([]);
  page = signal<number | null>(1);
  pageSize = signal(PaginationHelper.pageSize);
  visible = this.notificationService.sidebarOpen;
  isLoading = signal<boolean>(false);

  onVisibleChange(event: boolean): void {
    this.notificationService.toggleSidebar(event);
  }

  constructor() {
    super();
    effect(() => {
      if (this.visible() === false) {
        this.page.set(1);
        this.items.set([]);
      }
    });
    toObservable(this.visible).pipe(
      filter(Boolean),
      tap(() => {
        const page = this.page();
        if (page != null) this.loadNot(page);
      }),
    )
      .subscribe();

    this.websocketService.filterMessages(
      {
        type: 'resource.event',
        payload: { resource_type: 'notification' },
      },
      { payload: { context: { action_type: 'transport_emergency' } } },
    ).pipe(
      takeUntil(this.destroy),
      tap(() => {
        this.notificationService.setNotificationStats();
        this.messageService.add({
          severity: 'info',
          summary: 'New notification',
          detail: 'You have a new notification - open sidebar to check',
        });
      }),
    )
      .subscribe();
  }

  loadNot(page: number): void {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.notificationService.getNotifications(page)
      .pipe(
        tap(() => {
          this.isLoading.set(false);
        }),
        takeUntil(this.destroy),
      )
      .subscribe((res: Page<Notification>) => {
        this.items.set([
          ...this.items(),
          ...res.results,
        ]);
        this.page.set(res.nextPage ?? null);
      });
  }

  markAsRead(notification: Notification): void {
    this.notificationService.markAsRead(notification.id)
      .pipe(
        take(1),
        tap(() => this.notificationService.setNotificationStats()),
      )
      .subscribe((res: Notification) => {
        this.items.update((items: Notification[]) => {
          const index = items.findIndex(n => n.id === notification.id);
          if (index !== -1) {
            items[index] = res;
          }
          return items;
        });
      });
  }

  readAll(): void {
    this.notificationService.readAll()
      .pipe(
        take(1),
        tap(() => this.notificationService.setNotificationStats()),
      )
      .subscribe(() => {
        this.items.set([]);
        this.page.set(1);
        this.loadNot(1);
      });
  }

  onScroll(): void {
    const el = this.scrollerEl?.nativeElement;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    const nextPage = this.page();
    if (atBottom && nextPage != null) {
      this.loadNot(nextPage);
    }
  }

}
